import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { productImageUrl } from './AiLookResults';
import { formatProductPriceEur } from './currency';
import { setPageMeta } from './pageMeta';

export default function PublicLookPage() {
  const { id } = useParams();
  const { formatDate, locale, t } = useI18n();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    api.getPublicSeoLook(id)
      .then((response) => {
        if (mounted) setPayload(response.data || null);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'request_failed');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id]);

  const event = payload?.event;
  const items = useMemo(() => payload?.items || [], [payload?.items]);

  useEffect(() => {
    if (!event?.celebrity_name) return;
    const itemSummary = items
      .slice(0, 3)
      .map((item) => [item.color, item.category].filter(Boolean).join(' '))
      .filter(Boolean)
      .join(', ');
    setPageMeta({
      title: `${event.celebrity_name} Outfit Similar Products | Outfit Detective`,
      description: `Find similar products for this ${event.celebrity_name} outfit${itemSummary ? `, including ${itemSummary}` : ''}. Compare outfit items, alternatives and shopping matches inspired by the look.`,
      pathname: `/looks/${event.id}`,
      image: event.image_url,
    });
  }, [event, items]);

  function formatPrice(candidate) {
    return formatProductPriceEur(candidate, currencyFormatter, t('detail.priceUnavailable'));
  }

  if (loading) {
    return (
      <main className="ct-public-tryon-page">
        <div className="ct-card ct-empty">{t('common.loading')}</div>
      </main>
    );
  }

  if (error || !event) {
    return (
      <main className="ct-public-tryon-page">
        <section className="ct-public-tryon-missing">
          <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
          <h1>Look not found</h1>
          <p>This public look is not available yet.</p>
          <Link to="/" className="btn btn-dark">Open Outfit Detective</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="ct-public-tryon-page">
      <section className="ct-public-tryon-shell">
        <article className="ct-public-tryon-card">
          <div className="ct-public-tryon-copy">
            <p className="ct-kicker">Shop similar clothes</p>
            <h1>{event.celebrity_name} outfit similar products</h1>
            <p>
              Find similar clothes, outfit alternatives and shopping matches inspired by this {event.celebrity_name}
              look. These product suggestions are not guaranteed identical items or official endorsements.
            </p>
          </div>
          {event.image_url ? (
            <div className="ct-public-tryon-image">
              <img src={event.image_url} alt="" />
            </div>
          ) : null}
          <div className="ct-public-tryon-meta">
            <div>
              <strong>{event.title || `${event.celebrity_name} outfit`}</strong>
              <span>{formatDate(event.event_at, { dateStyle: 'medium' })}</span>
            </div>
            <div>
              <strong>{event.source_name || 'Social look'}</strong>
              <span>Outfit Detective match</span>
            </div>
          </div>
        </article>

        <section className="ct-public-seo-copy">
          <h2>Find similar clothes from this outfit</h2>
          <p>
            Outfit Detective scans the visible outfit items and groups similar products by item, color and style. Use
            this page as a celebrity outfit finder for alternatives, budget ideas and shopping inspiration.
          </p>
        </section>

        {items.map((item) => (
          <section className="ct-public-selected-products" key={item.id}>
            <strong>
              {[
                item.color,
                item.category,
                'similar products',
              ].filter(Boolean).join(' ') || item.description || 'Outfit item similar products'}
            </strong>
            {item.candidates.slice(0, 8).map((candidate) => {
              const content = (
                <>
                  {candidate.image_url ? <img src={productImageUrl(candidate.image_url)} alt="" /> : <i className="ph ph-shopping-bag"></i>}
                  <span>
                    <b>{candidate.name}</b>
                    <small>{candidate.store || candidate.brand || candidate.lane}</small>
                  </span>
                  <em>{formatPrice(candidate)}</em>
                </>
              );
              return candidate.product_url ? (
                <a href={candidate.product_url} target="_blank" rel="noreferrer" key={candidate.id}>
                  {content}
                </a>
              ) : (
                <span key={candidate.id}>{content}</span>
              );
            })}
            <p>Prices and availability can change. Check the retailer before buying.</p>
          </section>
        ))}

        <section className="ct-public-tryon-cta">
          <p className="ct-kicker">Find more looks</p>
          <h2>Discover more celebrity outfits and creator looks</h2>
          <p>
            Use Outfit Detective to follow creators, save looks, compare similar products and try outfit ideas from
            public style moments.
          </p>
          <div className="ct-public-tryon-cta-actions">
            <Link to={`/style/${event.celebrity_slug}`} className="btn btn-light">
              More {event.celebrity_name} outfits
            </Link>
            <Link to="/" className="btn btn-dark">Try Outfit Detective</Link>
          </div>
        </section>

        <section className="ct-public-faq">
          <h2>Similar products FAQ</h2>
          <details>
            <summary>Are these the exact clothes from the original outfit?</summary>
            <p>
              They are shopping matches and similar products based on visible outfit details. Availability, price and
              exact match quality can change by retailer.
            </p>
          </details>
          <details>
            <summary>Can I find clothes from an Instagram or TikTok outfit?</summary>
            <p>
              Outfit Detective is designed to help you discover outfit items and similar clothes from public creator
              and celebrity looks.
            </p>
          </details>
        </section>
      </section>
    </main>
  );
}
